import { useCallback, useRef, useState } from 'react';

/**
 * MediaRecorder-Huelle. Liefert einen Blob nach stop().
 * Kein Transkript im Client - das macht der Server (Slice 3).
 */
export function useAudioRecorder() {
  const [state, setState] = useState<'idle' | 'recording' | 'done' | 'unsupported' | 'denied'>(
    'idle',
  );
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setState('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      rec.onstop = () => {
        setBlob(new Blob(chunks.current, { type: mime }));
        stream.getTracks().forEach((t) => t.stop());
        setState('done');
      };
      rec.start();
      recorder.current = rec;
      setSeconds(0);
      setState('recording');
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setState('denied');
    }
  }, []);

  const stop = useCallback(() => {
    recorder.current?.stop();
    if (timer.current) clearInterval(timer.current);
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setSeconds(0);
    setState('idle');
  }, []);

  return { state, seconds, blob, start, stop, reset };
}
